import { api } from './client';

/**
 * Homingo Coins, as the server keeps them.
 *
 * ---------------------------------------------------------------------------
 * WHY THE APP DOES NOT DO COIN ARITHMETIC
 * ---------------------------------------------------------------------------
 * Every number below is computed server-side and read here verbatim. The tier
 * a customer sits in, the rate they earn at, what a coin is worth in rupees
 * and how much of a booking coins may pay for are all policy, and policy that
 * changes without an app release. An app that recomputed any of it would be
 * one deploy away from disagreeing with the wallet it is drawing.
 *
 * The one rule this file enforces: money-shaped values arrive as STRINGS
 * (`balanceValue`, `coinValueRupees`, `rupeeValue`) because they are Prisma
 * `Decimal` columns. Do not `Number()` them for display - format the string.
 * Coin counts are integers and safe to treat as numbers.
 */

/** Loyalty tiers, lowest first. Mirrors `WALLET_TIERS` on the backend. */
export type WalletTier = 'bronze' | 'silver' | 'gold' | 'platinum';

/** What `GET /customers/me/wallet` answers. */
export type WalletSummary = {
  /** Spendable coins. An integer. */
  balanceCoins: number;
  /** Rupee value of the balance, as a decimal string. */
  balanceValue: string;
  tier: WalletTier;
  /** Null when already at the top tier - render "highest tier", not "null". */
  nextTier: WalletTier | null;
  /** Null for the same reason as `nextTier`. */
  bookingsToNextTier: number | null;
  /** Percent of a booking earned back as coins at this tier. */
  earnRatePercent: number;
  completedBookings: number;
  lifetimeEarnedCoins: number;
  lifetimeRedeemedCoins: number;
  /** Rupee value of ONE coin, as a decimal string. */
  coinValueRupees: string;
  /** Ceiling on how much of any one booking coins may pay for. */
  maxRedemptionPercent: number;
  /**
   * Coins lapsing within 30 days, capped at the live balance.
   *
   * Zero is the common case and must render as nothing at all - a permanent
   * "0 coins expiring soon" badge trains people to ignore the one that matters.
   */
  expiringSoonCoins: number;
  /**
   * Coins left over from vouchers, locked until their orders are done.
   *
   * **Not** part of `balanceCoins` and not spendable - the cart will refuse
   * them, because the server has not credited them yet. Shown so a customer
   * who was promised them at checkout can see they are coming.
   *
   * Optional: a deployment older than the feature sends neither field.
   */
  pendingVoucherCoins?: number;
  pendingVouchers?: PendingVoucherCoins[];
};

/** One voucher's locked remainder, and what it is waiting on. */
export type PendingVoucherCoins = {
  redemptionId: string;
  /** The code that was typed. Null for an automatic offer. */
  code: string | null;
  coins: number;
  /** The bookings still standing that the coins are waiting on. */
  bookingNumbers: string[];
};

/** One movement in the coin statement. */
export type WalletTransaction = {
  id: string;
  /** `earn` | `redeem` | `adjustment` | `expiry` … shown via `reason`. */
  type: string;
  /** Signed: positive credits, negative debits. */
  coins: number;
  balanceAfter: number;
  /** Rupee value of this movement, as a decimal string. */
  rupeeValue: string;
  /**
   * Written by the backend to be shown to the customer VERBATIM.
   * Do not re-word it in the app - it names the booking and the rate applied.
   */
  reason: string;
  bookingId: string | null;
  /** ISO date-time, or null when these coins do not lapse. */
  expiresAt: string | null;
  /** ISO date-time. */
  createdAt: string;
};

/** A page of the statement. `nextCursor` is null on the last page. */
export type WalletStatement = {
  items: WalletTransaction[];
  nextCursor: string | null;
};

/** Balance, tier and the earn policy behind them. */
export const summary = (signal?: AbortSignal): Promise<WalletSummary> =>
  api.get<WalletSummary>('/customers/me/wallet', { signal });

/**
 * The coin statement, newest first.
 *
 * Cursor-paginated rather than offset: pass the `nextCursor` from the previous
 * page. Omitting it starts from the top.
 */
export const transactions = (
  options: { limit?: number; cursor?: string; signal?: AbortSignal } = {},
): Promise<WalletStatement> => {
  const query = new URLSearchParams();
  if (options.limit !== undefined) {
    query.set('limit', String(options.limit));
  }
  if (options.cursor) {
    query.set('cursor', options.cursor);
  }
  const suffix = query.toString() ? `?${query.toString()}` : '';
  return api.get<WalletStatement>(`/customers/me/wallet/transactions${suffix}`, {
    signal: options.signal,
  });
};

// ------------------------------------------------------------------ redeeming

/**
 * What coins are worth against ONE bill, as the server scores it.
 *
 * ---------------------------------------------------------------------------
 * THE APP MUST NOT WORK THIS OUT ITSELF
 * ---------------------------------------------------------------------------
 * The arithmetic looks trivial - balance × coin value, capped at a percentage
 * of the total - and every term in it is policy the server owns and changes
 * without an app release: the rate, the ceiling, whether a discounted or
 * subscription booking may redeem at all. An app that computed the figure
 * would quote a discount the checkout then refused, which is the one failure
 * a customer reads as being cheated.
 *
 * So the cart asks what this basket can redeem, shows exactly that, and sends
 * back the coin count it was told about.
 */
export type RedemptionQuote = {
  /** Most coins spendable on this bill. Integer, already capped. */
  maxCoins: number;
  /** Rupees those coins take off, as a decimal string. */
  maxValue: string;
  /** Live balance, so the cart need not hold a second copy. */
  balanceCoins: number;
  /**
   * Why nothing can be redeemed, when `maxCoins` is 0. Shown verbatim.
   * Null whenever redemption is available.
   */
  blockedReason: string | null;
};

/**
 * What this basket could redeem, before anything is booked.
 *
 * The total is sent in whole rupees - the same figure the cart is showing as
 * payable - because the ceiling is a percentage OF it and the server cannot
 * apply a percentage to a bill it has not seen.
 */
export const quoteRedemption = (
  totalAmount: number,
  signal?: AbortSignal,
): Promise<RedemptionQuote> =>
  api.get<RedemptionQuote>(
    `/customers/me/wallet/redemption?totalAmount=${totalAmount}`,
    { signal },
  );
